import { Sentry } from '@/services/sentry';
import { useUserStore } from '@/stores/user'
import { updateSettings } from '@/services/account'

/**
 * Composable for handling user settings updates with optimistic UI updates
 *
 * @returns {Object} Object containing handleSettingChange function
 *
 * @example
 * const { handleSettingChange } = useSettingsUpdate()
 *
 * const handleCountryChange = (event) => {
 *   handleSettingChange('country', event.detail.value, country.value)
 * }
 */
export function useSettingsUpdate() {
  const userStore = useUserStore()

  /**
   * Generic handler for updating any setting field
   * Performs optimistic update to local store, then syncs with backend
   * Reverts on error
   *
   * @param {string} fieldName - Name of the setting field (e.g., 'oddsFormat', 'country', 'timezone')
   * @param {any} newValue - New value for the field
   * @param {any} previousValue - Previous value (for rollback on error)
   */
  const handleSettingChange = async (fieldName, newValue, previousValue) => {
    // Update local store immediately for UI responsiveness (optimistic update)
    userStore.updateSettings({ [fieldName]: newValue })

    try {
      // Send POST request to backend - updateSettings will merge with current settings
      await updateSettings({ [fieldName]: newValue }, userStore.settings)
    } catch (error) {
      console.error(`❌ Failed to save ${fieldName}:`, error)
      Sentry.captureException(error);

      // Revert to previous value on error
      userStore.updateSettings({ [fieldName]: previousValue })

      // TODO: Show error toast to user
      // Example:
      // const toast = await toastController.create({
      //   message: `Failed to update ${fieldName}. Please try again.`,
      //   duration: 3000,
      //   color: 'danger'
      // })
      // await toast.present()
    }
  }

  return {
    handleSettingChange
  }
}
